import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Exportacao, GuiasRejeitadas } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridEvent, ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  AgFilterUtils,
  AgGridActionCellParams,
  AgGridActionCellRenderer,
  AgGridPaginationStatusBar,
  buildGridOptions,
  exportGridAsExcel,
  Page,
  PAGE_DEFAULT,
  PaginationStatus,
} from '@gestor-web/app/shared/components';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { GuiasRejeitadasService } from './guias-rejeitadas.service';
import {
  buildMenuExportacao,
  confirmarExportacaoAssincrona,
  isVisaoAgrupada,
  MOTIVO_BLOQUEIO_VISAO_AGRUPADA,
  tratarErroLimiteExportacao,
} from '@gestor-web/app/shared/utils/exportacao.utils';
import { stripGuiaFieldPrefix } from '@gestor-web/app/shared/utils/guias.utils';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { GuiasRejeitadasFilterData } from './components/filtro/filtro.component';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.GUIAS_REJEITADAS';
const GRID_KEY_AGREGADO = 'ANALISES.GUIAS_REJEITADAS.AGREGADO';

const FEATURE_KEY_EXPORTACAO = 'INCORPORACAO_DADOS.GUIAS_REJEITADAS.EXPORTAR_CSV';

const normalizeMultilineFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map(v => String(v).trim())
      .filter(Boolean)
      .join(' | ');
  }

  if (typeof value === 'string') {
    return value
      .replace(/\r?\n+/g, ' | ')
      .replace(/\s*\|\s*/g, ' | ')
      .trim();
  }

  if (value == null) return '';
  return String(value);
};

const setFilterTooltipRenderer = (params: { value: unknown }) => {
  const normalized = normalizeMultilineFilterValue(params?.value);
  const span = document.createElement('span');
  span.className = 'set-filter-tooltip-item';
  span.title = normalized;
  span.textContent = normalized;
  return span;
};

const MULTILINE_COLUMN: Pick<
  ColDef,
  'wrapText' | 'autoHeight' | 'cellClass' | 'filterValueGetter' | 'getQuickFilterText' | 'filterParams'
> = {
  wrapText: true,
  autoHeight: true,
  cellClass: 'cell-multiline',
  filterValueGetter: params => {
    const field = params.colDef.field;
    if (!field) return '';
    return normalizeMultilineFilterValue((params.data as any)?.[field]);
  },
  getQuickFilterText: params => normalizeMultilineFilterValue(params.value),
  filterParams: {
    cellRenderer: setFilterTooltipRenderer,
  },
};

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 160,
  },
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    width: 160,
    cellRenderer: AgGridActionCellRenderer,
    cellRendererParams: {
      actionTooltip: 'Exibir detalhes da guia',
    } as AgGridActionCellParams,
    type: 'str',
  },
  {
    headerName: '006-Registro ANS',
    field: 'cd_registro_ans',
    width: 160,
    hide: true,
  },
  {
    headerName: '022-Origem da guia',
    field: 'nm_id_origem_guia',
    width: 200,
  },
  {
    headerName: '012-CNES Executante',
    field: 'nr_cnes_exec',
    width: 180,
  },
  {
    headerName: '013-Tipo CPF/CNPJ',
    field: 'nm_tipo_cpf_cnpj',
    width: 180,
  },
  {
    headerName: '014-CNPJ/CPF Executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
    width: 200,
  },
  {
    headerName: '024-Nr. guia operadora',
    field: 'nr_guia_operadora',
    width: 200,
  },
  {
    headerName: '023-Nr. guia no prestador',
    field: 'nr_guia_prestador',
    width: 220,
  },
  {
    headerName: '025-Nr. reembolso ou pagto eventual',
    field: 'nr_reembolso',
    width: 280,
  },
  {
    headerName: '080-Data processamento',
    field: 'dt_processamento',
    type: 'date',
    width: 200,
  },
  {
    headerName: 'Sequencial lancto',
    field: 'nr_seq_lancto',
    width: 180,
  },
  {
    headerName: '021-Tipo de guia',
    field: 'nm_tp_guia',
    width: 180,
  },
  {
    headerName: '050-Valor informado',
    field: 'vl_guia_informado',
    type: 'money',
    width: 200,
  },
  {
    headerName: 'Nome arquivo no Gestor',
    field: 'nm_arquivo',
    width: 220,
  },
  {
    headerName: 'Erro no campo da guia',
    field: 'lt_campo_guia',
    width: 240,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Erro no campo do item',
    field: 'lt_campo_item',
    width: 240,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Rejeição do arquivo',
    field: 'rejeicao_arquivo',
    width: 260,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Rejeição da guia',
    field: 'rejeicao_guia',
    width: 280,
    ...MULTILINE_COLUMN,
  },
  {
    headerName: 'Rejeição do item',
    field: 'rejeicao_item',
    width: 280,
    ...MULTILINE_COLUMN,
  },
];

const COLUMNS_AGREGADO: ColDef[] = [
  {
    headerName: 'Nível da rejeição',
    field: 'visao',
    width: 220,
    enableRowGroup: true,
  },
  {
    headerName: 'Campo',
    field: 'campo',
    flex: 1,
    minWidth: 280,
    wrapText: true,
    autoHeight: true,
    cellClass: 'cell-multiline',
    enableRowGroup: true,
  },
  {
    headerName: 'Motivo',
    field: 'motivo',
    flex: 1,
    minWidth: 360,
    wrapText: true,
    autoHeight: true,
    cellClass: 'cell-multiline',
    enableRowGroup: true,
  },
  {
    headerName: '050-Valor informado',
    field: 'vl_guia_informado',
    type: 'money',
    aggFunc: 'sum',
    width: 220,
  },
  {
    headerName: 'Competência TISS',
    field: 'nr_competencia',
    type: 'competencia',
    width: 200,
  },
  {
    headerName: 'Contagem de guias',
    field: 'qt_guias',
    type: 'int',
    width: 200,
  },
  {
    headerName: 'Contagem de arquivos',
    field: 'qt_arquivos',
    type: 'int',
    width: 200,
  },
];
@Component({
    templateUrl: './guias-rejeitadas.component.html',
    styleUrls: ['./guias-rejeitadas.component.scss'],
    providers: [GuiasRejeitadasService, DecimalPipe],
    standalone: false
})
export class GuiasRejeitadasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly GRID_KEY_AGREGADO = GRID_KEY_AGREGADO;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;
  @ViewChild('agGridAgregado') agGridAgregado: AgGridAngular<RowAgregado>;

  tabIndex = 0;

  page: number = PAGE_DEFAULT.page;
  pageSize: number = PAGE_DEFAULT.limit;
  pageData: Page = PAGE_DEFAULT;
  paginationReset = false;
  pagination$: BehaviorSubject<PaginationStatus> = new BehaviorSubject<PaginationStatus>(null);

  gridOptions: GridOptions<Row>;
  gridOptionsAgregado: GridOptions<RowAgregado>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  firstLoadAgregado = true;
  filterChangedAgregado = true;
  rowDataAgregado: RowAgregado[] = [];

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRangeTab0: string[] = [];
  competenciaRangeTab1: string[] = [];
  appliedCompetenciaRange: string[] = [];

  get activeCompetenciaRange(): string[] {
    // console.log('[GuiasRejeitadas] activeCompetenciaRange')
    return this.tabIndex === 0 ? this.competenciaRangeTab0 : this.competenciaRangeTab1;
  }

  lancamentoOptions: SelectItem<string>[] = [];
  selectedLancamento: string = null;

  rowData: Row[] = [];
  rowTotal: number = 0;
  dtUltimoProcessamento: string = null;
  dtUltimoProcessamentoResumo: string = null;

  filterData: GuiasRejeitadasFilterData = { motivo_guia: [], motivo_item: [] };
  appliedFilterData: GuiasRejeitadasFilterData = { motivo_guia: [], motivo_item: [] };
  appliedSelectedLancamento: string = null;

  motivoGuiaOptions: SelectItem<string>[] = [];
  motivoItemOptions: SelectItem<string>[] = [];

  exportMenuItems: MenuItem[] = [];
  hasAcessoExportacao = false;

  guiasList: string[] = [];

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;
  detalheGuiaCodigoIndex: number;

  pageTooltip = 'ANALISES.GUIAS_REJEITADAS';

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private service: GuiasRejeitadasService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private confirmationService: ConfirmationService,
    private gridService: GridService,
    private geralService: GeralService,
    private permissaoService: PermissaoService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRangeTab0 = [`${ano}01`, `${ano}12`];
    this.competenciaRangeTab1 = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        const defaultRange = [
          params.competencia ? '' + params.competencia : this.competenciaRangeTab0[0],
          params.competencia_final ? '' + params.competencia_final : this.competenciaRangeTab0[1],
        ];
        this.competenciaRangeTab0 = [...defaultRange];
        this.competenciaRangeTab1 = [...defaultRange];

        setTimeout(() => {
          this.pageReady$.next();
          this.loadOpcoesFiltro();
        });
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (this.tabIndex === 1) {
          this.reloadDataAgregado();
        } else if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });

    this.lancamentoOptions = [
      { label: 'Todos', value: null },
      { label: 'Primeiro', value: 'P' },
      { label: 'Subsequente', value: 'S' },
    ];

    this.permissaoService
      .checkAcessoFeature(FEATURE_KEY_EXPORTACAO)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(hasAccesso => {
        // console.log('[Listagem] hasAccesso:', hasAccesso);
        this.hasAcessoExportacao = hasAccesso;
        this.atualizarMenuExportacao();
      });
  }

  abrirMenuExportacao(event: Event, menu: Menu): void {
    this.atualizarMenuExportacao();
    menu.toggle(event);
  }

  private atualizarMenuExportacao(): void {
    this.exportMenuItems = buildMenuExportacao({
      onExportarLinhasRetornadas: () => this.exportExcel(),
      onExportarTodas: () => this.showConfirmationExportAll(),
      assincronaVisivel: this.hasAcessoExportacao || true,
      assincronaDesabilitada: this.tabIndex !== 0,
      motivoBloqueioAssincrona: this.getMotivoBloqueioExportacaoAssincrona(),
    });
  }

  private getMotivoBloqueioExportacaoAssincrona(): string {
    return this.tabIndex === 0 && isVisaoAgrupada(this.agGrid?.api) ? MOTIVO_BLOQUEIO_VISAO_AGRUPADA : null;
  }

  private isExportacaoAssincronaBloqueada(): boolean {
    return this.tabIndex !== 0 || !!this.getMotivoBloqueioExportacaoAssincrona();
  }

  private configureGrid() {
    const columnDefs = COLUMNS;
    const colGuiaMonitor = columnDefs.find(col => col.field === 'cd_guia_monitor');

    colGuiaMonitor.cellRendererParams['actionFunction'] = (value: string) => {
      this.showDetalheGuiaFor(value);
    };

    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs,
        pipes: {
          date: this.datePipe,
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        onFirstDataRendered: event => this.updateGuiasList(event),
        onFilterChanged: event => this.updateGuiasList(event),
        onSortChanged: event => this.updateGuiasList(event),
        onRowDataUpdated: event => this.updateGuiasList(event),
        statusBar: {
          statusPanels: [
            {
              statusPanel: AgGridPaginationStatusBar,
              statusPanelParams: { pagination$: this.pagination$ },
              align: 'left',
            },
          ],
        },
      },
    );

    this.gridOptionsAgregado = buildGridOptions<RowAgregado>(
      {
        columnDefs: COLUMNS_AGREGADO,
        pipes: {
          date: this.datePipe,
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY_AGREGADO,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        grandTotalRow: 'bottom',
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, tab, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRangeTab0[0] = competencia;
      this.competenciaRangeTab1[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRangeTab0[1] = competencia_final;
      this.competenciaRangeTab1[1] = competencia_final;
    }

    if (tab) {
      this.tabIndex = +tab;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    if (this.tabIndex === 1) {
      setTimeout(() =>
        this.gridService.applyVisaoInicial(GRID_KEY_AGREGADO, this.agGridAgregado, visao, sort, filterModel),
      );
    } else {
      setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  resetPagination() {
    this.page = 0;
    this.pageData = {
      ...this.pageData,
      page: 0,
      size: this.pageSize,
      offset: 0,
      limit: this.pageSize,
    };

    this.filterChanged = true;
    this.filterChangedAgregado = true;
  }

  competenciaChanged(range: string[]) {
    if (this.tabIndex === 0) {
      this.competenciaRangeTab0 = range;
      this.filterChanged = true;
    } else {
      this.competenciaRangeTab1 = range;
      this.filterChangedAgregado = true;
    }
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.page = 0;
    this.pageData = { ...this.pageData, page: 0, size: this.pageSize, offset: 0, limit: this.pageSize };
    this.loadOpcoesFiltro();
  }

  reloadData(applyPendingFilters: boolean = false) {
    const shouldApplyFilters = applyPendingFilters || !this.appliedCompetenciaRange?.length;
    if (shouldApplyFilters) {
      this.appliedCompetenciaRange = [...this.competenciaRangeTab0];
      this.appliedSelectedLancamento = this.selectedLancamento;
      this.appliedFilterData = { ...this.filterData };
      this.filterChanged = false;

      this.loadOpcoesFiltro();
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGrid?.api?.setGridOption('loading', true));
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.appliedCompetenciaRange[0] || !this.appliedCompetenciaRange[1]) {
      this.rowData = [];
      this.rowTotal = 0;
      this.dtUltimoProcessamento = null;
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      this.agGrid?.api?.setGridOption('loading', false);
      this.paginationReset = false;
      this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      return;
    }

    this.pageData = {
      page: this.page,
      size: this.pageSize,
      offset: this.page * this.pageSize,
      limit: this.pageSize,
    };

    const grid$ = this.service.getDataGoApi(
      this.operadora,
      this.appliedCompetenciaRange[0],
      this.appliedCompetenciaRange[1],
      {
        tpSeqLancto: this.appliedSelectedLancamento,
        motivoGuia: this.appliedFilterData.motivo_guia,
        motivoItem: this.appliedFilterData.motivo_item,
      },
      this.pageData,
    );

    const ultimo$ = this.service.getUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({
      grid: grid$,
      ultimo: ultimo$,
    }).subscribe({
      next: ({ grid, ultimo }) => {
        const { items, total } = grid;
        this.rowTotal = total ?? 0;
        this.dtUltimoProcessamento = this.getDataUltimoProcessamento(ultimo);

        this.rowData = (items ?? []).map(item => this.normalizeRow(item));
        this.paginationReset = false;
        this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  onTabChange(index: number) {
    this.tabIndex = index;
    this.atualizarMenuExportacao();
  }

  reloadDataAgregado() {
    this.filterChangedAgregado = false;

    const range = this.competenciaRangeTab1;

    if (this.firstLoadAgregado) {
      this.firstLoadAgregado = false;
      this.agGridAgregado?.api?.setGridOption('loading', false);
      setTimeout(() => this.agGridAgregado?.api?.setGridOption('loading', true));
    } else {
      this.agGridAgregado?.api?.setGridOption('loading', true);
    }

    if (!range[0] || !range[1]) {
      this.rowDataAgregado = [];
      this.agGridAgregado?.api?.setGridOption('loading', false);
      return;
    }

    // A aba Resumo não usa o filtro de motivo (removido); filtragem por Campo/Motivo é
    // feita pelos filtros de coluna do próprio grid. Mantém só competência + lançamento.
    const grid$ = this.service.getDataAgregadoGoApi(this.operadora, range[0], range[1], {
      tpSeqLancto: this.selectedLancamento,
      motivoGuia: [],
      motivoItem: [],
    });
    const ultimo$ = this.service.getUltimoProcessamentoResumo(this.operadora).pipe(catchError(() => of(null)));

    forkJoin({ grid: grid$, ultimo: ultimo$ }).subscribe({
      next: ({ grid, ultimo }) => {
        const resumo = grid ?? { rejeicao_arquivo: [], rejeicao_guia: [], rejeicao_item: [] };
        this.rowDataAgregado = [
          ...resumo.rejeicao_arquivo.map(l =>
            this.toResumoRow(
              'Rejeição do arquivo',
              '',
              this.buildRejeicaoArquivo(l.cd_motivo_arquivo, l.motivo),
              l.vl_guia_informado,
              l.qt_guias,
              l.qt_arquivos,
              l.nr_competencia,
            ),
          ),
          ...resumo.rejeicao_guia.map(l => {
            const { campo, motivo } = this.splitCampoMotivo(l.motivo);
            return this.toResumoRow(
              'Rejeição da guia',
              campo,
              motivo,
              l.vl_guia_informado,
              l.qt_guias,
              l.qt_arquivos,
              l.nr_competencia,
            );
          }),
          ...resumo.rejeicao_item.map(l => {
            const { campo, motivo } = this.splitCampoMotivo(l.motivo);
            return this.toResumoRow(
              'Rejeição do item',
              campo,
              motivo,
              l.vl_guia_informado,
              l.qt_guias,
              l.qt_arquivos,
              l.nr_competencia,
            );
          }),
        ];
        this.dtUltimoProcessamentoResumo = this.getDataUltimoProcessamento(ultimo);
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGridAgregado?.api?.setGridOption('loading', false),
    });
  }

  // O backend manda "077-Nome do campo|5053-Descrição do motivo"; separa nas 2 colunas.
  private splitCampoMotivo(raw: string): { campo: string; motivo: string } {
    const value = raw ?? '';
    const idx = value.indexOf('|');
    if (idx < 0) return { campo: '', motivo: value };
    return { campo: value.slice(0, idx), motivo: value.slice(idx + 1) };
  }

  private toResumoRow(visao: string, campo: string, motivo: string, valor: unknown, qt_guias?: number, qt_arquivos?: number, nr_competencia?: string): RowAgregado {
    return {
      visao,
      campo: this.dashIfEmpty(campo),
      motivo: this.dashIfEmpty(motivo),
      vl_guia_informado: this.toNumber(valor) ?? 0,
      qt_guias, 
      qt_arquivos,
      nr_competencia
    };
  }

  // Padroniza célula sem valor (campo vazio, ou motivo "(Vazio)" do bucket missing) como "-".
  private dashIfEmpty(value: string): string {
    const v = (value ?? '').trim();
    return v === '' || v === '(Vazio)' ? '-' : v;
  }

  private normalizeRow(item: GuiasRejeitadas): Row {
    const raw: any = item ?? {};
    const tipoCpfCnpjRaw = raw.id_cnpj_cpf_exec ?? raw.tp_cpf_cnpj ?? raw.tipo_cpf_cnpj;
    const idOrigemGuiaRaw = raw.id_origem_guia ?? raw.origem_guia;
    const camposGuia = this.listToString(raw.campos_guia);
    const motivosGuia = this.listToString(raw.motivos_guia);
    const camposItem = this.listToString(raw.campos_item);
    const motivosItem = this.listToString(raw.motivos_item);
    const rejeicaoArquivo = this.buildRejeicaoArquivo(raw.cd_motivo_arquivo, raw.ds_motivo_arquivo);
    const rejeicaoGuia = this.buildRejeicoesCodigoMotivo(raw.lt_motivo_guia ?? motivosGuia);
    const rejeicaoItem = this.buildRejeicoesCodigoMotivo(raw.lt_motivo_item ?? motivosItem);

    const row: Row = {
      ...raw,
      cd_registro_ans: raw.cd_registro_ans ?? raw.nr_registro_ans ?? '',
      cd_guia_monitor: raw.cd_guia_monitor ?? '',
      nr_cnes_exec: raw.nr_cnes_exec ?? raw.nr_cnes ?? '',
      nr_cnpj_cpf_exec: raw.nr_cnpj_cpf_exec ?? raw.cpf_cnpj ?? '',
      nr_seq_lancto: raw.nr_seq_lancto ?? raw.nr_seq_guia ?? raw.sequencial_lancto ?? null,
      lt_campo_guia: raw.lt_campo_guia ?? camposGuia,
      lt_motivo_guia: raw.lt_motivo_guia ?? motivosGuia,
      lt_campo_item: raw.lt_campo_item ?? camposItem,
      lt_motivo_item: raw.lt_motivo_item ?? motivosItem,
      vl_guia_informado: this.toNumber(raw.vl_guia_informado),
      cd_motivo_arquivo: raw.cd_motivo_arquivo ?? null,
      ds_motivo_arquivo: raw.ds_motivo_arquivo ?? '',
      rejeicao_arquivo: rejeicaoArquivo,
      rejeicao_guia: rejeicaoGuia,
      rejeicao_item: rejeicaoItem,
      nm_tipo_cpf_cnpj: '',
      nm_tp_guia: '',
      nm_id_origem_guia: '',
    };

    row.nm_tipo_cpf_cnpj = this.getNomeTipoCpfCnpj(tipoCpfCnpjRaw);
    row.nm_tp_guia = this.getNomeTipoGuia(row.tp_guia);
    row.nm_id_origem_guia = this.getNomeOrigemGuia(idOrigemGuiaRaw);

    return row;
  }

  private listToString(value: unknown): string {
    if (Array.isArray(value)) {
      return value
        .map(v => String(v).trim())
        .filter(Boolean)
        .join('\n');
    }
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) return '';
      if (normalized.includes(';')) {
        return normalized
          .split(';')
          .map(v => v.trim())
          .filter(Boolean)
          .join('\n');
      }
      if (normalized.includes(':')) {
        return normalized
          .split(':')
          .map(v => v.trim())
          .filter(Boolean)
          .join('\n');
      }
      return normalized;
    }
    return '';
  }

  private buildRejeicaoArquivo(codigo: unknown, motivo: unknown): string {
    const cd = codigo == null ? '' : String(codigo).trim();
    const ds = motivo == null ? '' : String(motivo).trim();

    if (cd && ds) return `${cd} - ${ds}`;
    if (cd) return cd;
    if (ds) return ds;
    return '';
  }

  private buildRejeicoesCodigoMotivo(value: unknown): string {
    const values = this.extractList(value);
    if (!values.length) return '';

    const parsed = values
      .map(item => stripGuiaFieldPrefix(item))
      .map(item => item.trim())
      .filter(Boolean);

    return Array.from(new Set(parsed)).join('\n');
  }

  private extractList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map(v => String(v).trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
      return value
        .split(/[\r\n;]+/)
        .map(v => v.trim())
        .filter(Boolean);
    }

    return [];
  }

  private getNomeTipoCpfCnpj(tipoCpfCnpjRaw: string | number | null | undefined): string {
    if (tipoCpfCnpjRaw == null || tipoCpfCnpjRaw === '') return '';
    return this.geralService.buildNomeDeDominio(tipoCpfCnpjRaw, 'MON_ID_CNPJ_CPF') || String(tipoCpfCnpjRaw);
  }

  private getNomeTipoGuia(tipoGuiaRaw: string | number | null | undefined): string {
    if (tipoGuiaRaw == null || tipoGuiaRaw === '') return '';
    return this.geralService.buildNomeDeDominio(tipoGuiaRaw, 'MON_TIPO_GUIA') || String(tipoGuiaRaw);
  }

  private getNomeOrigemGuia(origemRaw: string | number | null | undefined): string {
    if (origemRaw == null || origemRaw === '') return '';
    return (
      this.geralService.buildNomeDeDominio(origemRaw, 'MON_ORIGEM_GUIA') ||
      this.geralService.buildNomeDeDominio(origemRaw, 'dm_origemEventoAtencaoSaude') ||
      String(origemRaw)
    );
  }

  private toNumber(value: unknown): number | null {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private getDataUltimoProcessamento(
    ultimoProcessamentoResponse: { items?: Array<{ dt_atualizacao_mon_guia?: string | number }> } | null,
  ): string {
    const valor = ultimoProcessamentoResponse?.items?.[0]?.dt_atualizacao_mon_guia;
    if (!valor) return null;

    const timestamp = Number(valor);
    return this.datePipe.transform(Number.isNaN(timestamp) ? valor : timestamp, 'dd/MM/yyyy');
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  private updateGuiasList(event: AgGridEvent<Row>) {
    const guias = new Set<string>();
    event.api.forEachNodeAfterFilterAndSort(node => {
      if (node.data?.cd_guia_monitor) {
        guias.add(node.data.cd_guia_monitor);
      }
    });
    this.guiasList = Array.from(guias);
  }

  private loadOpcoesFiltro() {
    const range =
      this.tabIndex === 0
        ? this.appliedCompetenciaRange?.length
          ? this.appliedCompetenciaRange
          : this.competenciaRangeTab0
        : this.competenciaRangeTab1;

    if (!range[0] || !range[1]) return;

    this.service.getOpcoesFiltro(this.operadora, range[0], range[1]).subscribe(opcoes => {
      this.motivoGuiaOptions = (opcoes?.motivos_guia ?? []).sort().map(v => ({ label: v, value: v }));
      this.motivoItemOptions = (opcoes?.motivos_item ?? []).sort().map(v => ({ label: v, value: v }));
    });
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
    this.detalheGuiaCodigoIndex = this.guiasList.indexOf(cd_guia_monitor);
  }

  showDetalheGuiaForNext() {
    if (this.detalheGuiaCodigoIndex > this.guiasList.length - 2) return;
    this.detalheGuiaCodigoIndex++;
    this.detalheGuiaCodigo = this.guiasList[this.detalheGuiaCodigoIndex];
  }

  showDetalheGuiaForPrev() {
    if (this.detalheGuiaCodigoIndex < 1) return;
    this.detalheGuiaCodigoIndex--;
    this.detalheGuiaCodigo = this.guiasList[this.detalheGuiaCodigoIndex];
  }

  onPageChange(page: Page) {
    this.pageData = page;
    this.page = page.page;
    this.pageSize = page.size;

    this.reloadData();
  }

  onSearch(value: string) {
    if (this.tabIndex === 1) {
      this.agGridAgregado?.api?.updateGridOptions({ quickFilterText: value });
    } else {
      this.agGrid?.api?.updateGridOptions({ quickFilterText: value });
    }
  }

  exportExcel(): void {
    const isAgregado = this.tabIndex === 1;
    const competencia = isAgregado
      ? this.competenciaRangeTab1
      : this.appliedCompetenciaRange?.length
        ? this.appliedCompetenciaRange
        : this.competenciaRangeTab0;

    const fileNamePrefix = `guias-rejeitadas${isAgregado ? '-agregado' : ''}-${this.operadoraNome}-${competencia[0]}-${competencia[1]}`;

    const headers = [
      isAgregado ? 'Guias rejeitadas - Agregado' : 'Guias rejeitadas',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(competencia),
    ];

    exportGridAsExcel({
      api: isAgregado ? this.agGridAgregado.api : this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  showConfirmationExportAll() {
    if (this.isExportacaoAssincronaBloqueada()) {
      console.error('Exportação assíncrona indisponível no estado atual da tela');
      return;
    }
    confirmarExportacaoAssincrona({
      confirmationService: this.confirmationService,
      totalLinhas: this.rowTotal,
      totalColunas: this.agGrid.api.getAllDisplayedColumns().length,
      formatarNumero: valor => this.formatarNumero(valor),
      onConfirm: () => this.exportExcelRequest(),
    });
  }

  private formatarNumero(valor: number): string {
    return this.decimalPipe.transform(valor) ?? String(valor);
  }

  exportExcelRequest(): void {
    if (this.isExportacaoAssincronaBloqueada()) {
      console.error('Exportação assíncrona indisponível no estado atual da tela');
      return;
    }
    const cols = this.agGrid.api.getAllDisplayedColumns();
    const campos_order = cols.map(col => col.getColDef().field);
    const campos_map = cols.reduce(
      (m, col) => {
        m[col.getColDef().field] = col.getColDef().headerName;
        return m;
      },
      {} as Record<string, string>,
    );

    const params: Exportacao = {
      campos_order,
      campos_map,
      filtros: {
        ...this.appliedFilterData,
        tp_seq_lancto: this.appliedSelectedLancamento,
        competencia: this.appliedCompetenciaRange[0],
        competencia_final: this.appliedCompetenciaRange[1],
      },
    };

    this.service.requestExportacaoGoApi(this.operadora, params).subscribe({
      next: () =>
        this.notificationService.info('O arquivo está sendo gerado. Você pode continuar usando o sistema normalmente.'),
      error: err => {
        const bloqueado = tratarErroLimiteExportacao({
          confirmationService: this.confirmationService,
          erro: err,
          formatarNumero: valor => this.formatarNumero(valor),
        });
        if (!bloqueado) {
          this.notificationService.handleError(err);
        }
      },
    });
  }
  

  onCopyUrlFilters() {
    const isAgregado = this.tabIndex === 1;
    const gridKey = isAgregado ? GRID_KEY_AGREGADO : GRID_KEY;
    const grid: AgGridAngular = isAgregado ? this.agGridAgregado : this.agGrid;

    const visao = this.gridService.getCurrentVisao(gridKey);
    const sort = this.gridService.getSimpleSort(grid);
    const extraFilters = this.gridService.getEncodedFilter(grid);
    const competencia = isAgregado
      ? this.competenciaRangeTab1
      : this.appliedCompetenciaRange?.length
        ? this.appliedCompetenciaRange
        : this.competenciaRangeTab0;

    const filter = {
      competencia: competencia[0],
      competencia_final: competencia[1],
      tab: isAgregado ? this.tabIndex : undefined,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();

  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends GuiasRejeitadas {
  nm_tipo_cpf_cnpj: string;
  nm_tp_guia: string;
  nm_id_origem_guia: string;
  rejeicao_arquivo: string;
  rejeicao_guia: string;
  rejeicao_item: string;
}

interface RowAgregado {
  visao: string;
  campo: string;
  motivo: string;
  vl_guia_informado: number;
  qt_guias: number;
  qt_arquivos: number;
  nr_competencia: string;
}
